import { useState, useMemo } from "react";

export const useSortableTable = (data = [], defaultKey = null) => {
  const [sortConfig, setSortConfig] = useState({
    key: defaultKey,
    direction: "asc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: "asc",
      };
    });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const isNumber = (val) =>
      val !== null &&
      val !== undefined &&
      val !== "" &&
      String(val).trim() !== "" &&
      !isNaN(Number(val));

    return [...data].sort((a, b) => {
      let aVal = a?.[sortConfig.key];
      let bVal = b?.[sortConfig.key];

      // NULL / EMPTY VALUES
      const aEmpty =
        aVal === null ||
        aVal === undefined ||
        String(aVal).trim() === "";

      const bEmpty =
        bVal === null ||
        bVal === undefined ||
        String(bVal).trim() === "";

      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      // NUMBER SORT
      const aIsNum = isNumber(aVal);
      const bIsNum = isNumber(bVal);

      if (aIsNum && bIsNum) {
        const aNum = Number(aVal);
        const bNum = Number(bVal);

        return sortConfig.direction === "asc"
          ? aNum - bNum
          : bNum - aNum;
      }

      // DATE SORT (DD-MM-YYYY)
      const dateRegex = /^\d{2}-\d{2}-\d{4}$/;

      if (
        typeof aVal === "string" &&
        typeof bVal === "string" &&
        dateRegex.test(aVal) &&
        dateRegex.test(bVal)
      ) {
        const [aDay, aMonth, aYear] = aVal.split("-");
        const [bDay, bMonth, bYear] = bVal.split("-");

        const aDate = new Date(`${aYear}-${aMonth}-${aDay}`);
        const bDate = new Date(`${bYear}-${bMonth}-${bDay}`);

        return sortConfig.direction === "asc"
          ? aDate - bDate
          : bDate - aDate;
      }

      // STRING SORT
      aVal = aVal.toString().toLowerCase().trim();
      bVal = bVal.toString().toLowerCase().trim();

      if (aVal < bVal) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }

      if (aVal > bVal) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }

      return 0;
    });
  }, [data, sortConfig]);

  return {
    sortedData,
    sortConfig,
    handleSort,
  };
};